import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Users, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  degree: "Degree",
  discipline: "Discipline",
  methodology: "Methodology",
  methodology_subtype: "Methodology Focus",
  doctoral_stage: "Doctoral Stage",
};
const FILTERS = ["recommended", "all", "degree", "discipline", "methodology", "doctoral_stage"];

export default function Circles() {
  const [tab, setTab] = useState("recommended");
  const [q, setQ] = useState("");
  const [recommended, setRecommended] = useState([]);
  const [all, setAll] = useState([]);
  const [busy, setBusy] = useState(null); // id being joined/left

  const load = useCallback(async () => {
    try {
      const [rec, list] = await Promise.all([
        api.get("/circles/recommended"),
        api.get("/circles"),
      ]);
      setRecommended(rec.data);
      setAll(list.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = () => {
    let src = tab === "recommended" ? recommended : all;
    if (tab !== "recommended" && tab !== "all") src = all.filter((c) => c.category === tab);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      src = src.filter((c) => c.name.toLowerCase().includes(needle) || (c.description || "").toLowerCase().includes(needle));
    }
    return src;
  };

  const join = async (c) => {
    setBusy(c.id);
    try {
      await api.post(`/circles/${c.id}/join`);
      toast.success(`Joined ${c.name}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const leave = async (c) => {
    setBusy(c.id);
    try {
      await api.post(`/circles/${c.id}/leave`);
      toast.success(`Left ${c.name}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const items = filtered();

  return (
    <div className="p-8 lg:p-12 max-w-6xl" data-testid="circles-page">
      <p className="text-xs uppercase tracking-[0.25em] text-primary">Circles · Not feeds</p>
      <h1 className="font-serif text-4xl lg:text-5xl font-black tracking-tight mt-3">Find your people.</h1>
      <p className="text-muted-foreground mt-3 max-w-2xl">Doctoral candidates gather here around the things that actually shape a scholar's day: degree, discipline, methodology, and current stage.</p>

      <div className="mt-8 flex flex-wrap gap-3 items-center">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setTab(f)}
            data-testid={`circle-tab-${f}`}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-colors ${
              tab === f ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {f === "recommended" ? "Recommended" : f === "all" ? "All" : CATEGORY_LABELS[f] || f}
          </button>
        ))}
        <div className="ml-auto relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="circle-search"
            placeholder="Search circles"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 w-64"
          />
        </div>
      </div>

      {tab === "recommended" && (
        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          Recommendations use your profile only — no algorithms, no AI.
        </div>
      )}

      {items.length === 0 && (
        <Card className="mt-10 p-10 text-center" data-testid="circles-empty-state">
          <Users className="h-8 w-8 text-muted-foreground mx-auto" />
          <p className="mt-4 font-serif text-2xl font-black tracking-tight">
            {tab === "recommended" ? "Finish onboarding to see recommendations." : "No Circles match that filter yet."}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            {tab === "recommended"
              ? "Add your discipline, methodology, and stage on your profile — we'll surface Circles that fit."
              : "Try a broader filter, or clear your search."}
          </p>
        </Card>
      )}

      <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="circles-grid">
        {items.map((c) => (
          <Card key={c.id} className="p-6" data-testid={`circle-card-${c.slug}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{CATEGORY_LABELS[c.category] || c.category}</p>
                <h3 className="font-serif text-2xl font-black tracking-tight mt-1">
                  <Link to={`/circles/${c.id}`} className="hover:underline underline-offset-4" data-testid={`circle-open-${c.slug}`}>{c.name}</Link>
                </h3>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{c.description}</p>
                <p className="text-xs text-muted-foreground mt-3">{c.member_count} {c.member_count === 1 ? "member" : "members"}</p>
                {c.reasons && c.reasons.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {c.reasons.map((r, i) => (
                      <p key={i} className="text-[11px] text-primary/80 leading-snug" data-testid={`circle-reason-${c.slug}`}>· {r}</p>
                    ))}
                  </div>
                )}
              </div>
              <div className="shrink-0">
                {c.is_member ? (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy === c.id}
                    onClick={() => leave(c)}
                    className="rounded-full"
                    data-testid={`circle-leave-${c.slug}`}
                  >
                    Leave
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    disabled={busy === c.id}
                    onClick={() => join(c)}
                    className="rounded-full"
                    data-testid={`circle-join-${c.slug}`}
                  >
                    Join Circle
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
